import React, { Component } from "react";
import { TenantContractInfo } from "@eres/ejari-api/Entity/tenant";
import Strings from "@my-dld/assets/strings";
import { TenantRequestAction, TenantRequestStatus, TenantRequestUserType } from "@eres/ejari-core/constants";
import ConfirmationPopup from "@my-dld/components/Popup/ConfirmationPopup";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { TenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import PayForContract from "../Contract/PayForContract";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { TenantDocumentLookup } from "@eres/ejari-api/Entity";

interface Props {
	userTypeId: string;
	contract: TenantContractInfo;
	onActionComplete: () => void;
	canResubmit: boolean;
}

interface State {
	confirmCancel?: boolean;
}

class TenantRequestActions extends Component<Props, State> {
	static contextType = TenantContractContext;
	context!: React.ContextType<typeof TenantContractContext>;
	pay = React.createRef<PayForContract>();

	constructor( props: Props ) {
		super( props );
		this.state = {};
	}

	render() {
		const { userTypeId, contract, canResubmit } = this.props;
		const { confirmCancel } = this.state;
		if ( userTypeId === "" + TenantRequestUserType.TENANT ) {
			return <div className="mb-3">
				{ contract.Status === TenantRequestStatus.PENDING &&
					<RenderPendingActions onCancel={ this.showCancelConfirmation }/> }
				{ contract.Status === TenantRequestStatus.REQUEST_FOR_UPDATE &&
					<RenderModificationActions
						onCancel={ this.showCancelConfirmation }
						onReSubmit={ this.onReSubmit } canResubmit={ canResubmit }/> }
				{ contract.Status === TenantRequestStatus.PAYMENT_PENDING &&
					<RenderPaymentActions
						onPay={ this.onPay }
						onCancel={ this.showCancelConfirmation }/> }
				{ confirmCancel && <ConfirmationPopup
					message={ Strings.alert_cancel }
					onConfirm={ this.onCancel }
					onCancel={ this.hideCancelConfirmation }/> }
				<PayForContract contractNumber={ contract.ContractNumber! } ref={ this.pay }/>
			</div>;
		}
		return null;
	}

	private showCancelConfirmation = () => {
		this.setState( { confirmCancel: true } );
	};

	private hideCancelConfirmation = () => {
		this.setState( { confirmCancel: false } );
	};

	private onCancel = () => {
		showLoading();
		const { contract } = this.props;
		if ( contract.RequestActionType === TenantRequestAction.TERMINATE ) {
			this.updateTerminateAction( TenantRequestStatus.CANCEL );
		} else {
			this.updateAction( TenantRequestStatus.CANCEL );
		}
	};

	private onReSubmit = () => {
		const { contract } = this.props;
		if ( this.isDocumentsAdded() ) {
			showLoading();
			if ( contract.RequestActionType === TenantRequestAction.TERMINATE ) {
				this.updateTerminateAction( TenantRequestStatus.SUBMIT );
			} else {
				this.updateAction( TenantRequestStatus.SUBMIT );
			}
		} else {
			showErrorMessage( Strings.document_required );
		}
	};

	private onPay = () => {
		const { onActionComplete } = this.props;
		this.pay.current?.initPayment( ( isSuccess, data ) => {
			if ( isSuccess ) {
				onActionComplete();
			}
		} );
	};

	private updateAction( status: number ) {
		const { userTypeId, onActionComplete } = this.props;
		this.trackEvent( status );
		this.context.updateTenancyRequestAction( this.context, userTypeId, status! ).then( () => {
			this.trackEvent( status, true );
			hideLoading();
			onActionComplete();
		} ).catch( e => {
			this.trackEvent( status, false );
			hideLoading();
			showErrorMessage( e.toString() );
		} );
	}

	private updateTerminateAction( status: number ) {
		const { userTypeId, onActionComplete } = this.props;
		this.trackEvent( status );
		this.context.updateTenancyTerminateRequestAction( this.context, userTypeId, status! ).then( () => {
			this.trackEvent( status, true );
			hideLoading();
			onActionComplete();
		} ).catch( e => {
			this.trackEvent( status, false );
			hideLoading();
			showErrorMessage( e.toString() );
		} );
	}

	private trackEvent( status: number, isSuccess?: boolean ) {
		const { contract } = this.props;
		let result = "Try";
		if ( isSuccess !== undefined ) {
			result = isSuccess ? "Success" : "Failed";
		}
		switch ( status ) {
			case TenantRequestStatus.CANCEL:
				trackEventWithData( "TenantContract", "TenantCancel" + result,
					{ actionType: contract.RequestActionType } );
				break;

			case TenantRequestStatus.SUBMIT:
				trackEventWithData( "TenantContract", "TenantReSubmit" + result,
					{ actionType: contract.RequestActionType } );
				break;
		}
	}

	private isDocumentsAdded() {
		let isValid = true;
		const { documentInfo } = this.context;
		if ( documentInfo && documentInfo.Documents ) {
			documentInfo.Documents.map( ( doc: TenantDocumentLookup ) => {
				if ( doc.IsMandatory === 1 && ( doc.Docs == null || doc.Docs.length === 0 ) ) {
					isValid = false;
				}
			} );
		}
		return isValid;
	}
}

function RenderPendingActions( props: { onCancel: () => void } ) {
	const { onCancel } = props;
	return <div className="mb-3 text-end">
		<button
			className="btn btn-grey" type="button"
			onClick={ onCancel }>{ Strings.cancel_request }</button>
	</div>;
}

function RenderModificationActions( props: { onReSubmit: () => void, onCancel: () => void, canResubmit: boolean } ) {
	const { onReSubmit, onCancel, canResubmit } = props;
	return <div className="mb-3 text-end">
		<button
			className="btn btn_1 me-2" type="button" disabled={ !canResubmit }
			onClick={ onReSubmit }>{ Strings.re_submit }</button>
		<button
			className="btn btn-grey ms-2" type="button"
			onClick={ onCancel }>{ Strings.cancel_request }</button>
	</div>;
}

function RenderPaymentActions( props: { onPay: () => void, onCancel: () => void } ) {
	const { onPay, onCancel } = props;
	return <div className="mb-3 text-end">
		<button
			className="btn btn_1 me-2" type="button"
			onClick={ onPay }>{ Strings.pay }</button>
		<button
			className="btn btn-grey ms-2 " type="button"
			onClick={ onCancel }>{ Strings.cancel_request }</button>
	</div>;
}

export default TenantRequestActions;
